import { useMemo } from 'react';
import Image from 'next/image';
import { DealSkuCardType } from '~/components/sku';
import { useGetSkuBannerDetails } from '~/lib/browse/hooks';
import useUser from '~/lib/user';
import clsx from 'clsx';

import {
  BLACK_FRIDAY_LAST_CALL_PARTICIPATION_SLUG,
  BLACK_FRIDAY_PARTICIPATION_SLUG,
  BLACK_FRIDAY_SLUG,
  SUMO_DAY_PARTICIPATION_SLUG,
  BLACK_FRIDAY_LAST_CALL_SLUG,
  SUMO_DAY_SLUG,
} from '~/lib/campaigns/constants';

import blackFridayBadge from '~/public/black-friday-badge-bg.svg';
import sumodayBadge from '~/public/sumoday-badge.svg';

function SkuBanner({ deal }: { deal: DealSkuCardType }) {
  const { bannerText, bannerColor } = useGetSkuBannerDetails(deal);

  return (
    <div
      className={`absolute bottom-0 left-0 w-full ${bannerColor} text-center font-bold text-white`}
    >
      {bannerText}
    </div>
  );
}

/**
 * Render the image and associated banners of a deal.
 */
export const DealImage = ({
  deal,
  width,
  height,
  priority = false,
  className = '',
  isSpotlight = false,
}: {
  deal: DealSkuCardType;
  width: number;
  height: number;
  priority: boolean;
  className?: string;
  isSpotlight?: boolean;
}) => {
  const { session } = useUser();

  const attrs: any = {};

  // usually the first SKU card is priority for mobile LCP if it above the fold
  if (priority) {
    attrs.priority = true;
  }

  const isBlackFriday = useMemo(
    () =>
      session?.campaigns?.includes(BLACK_FRIDAY_SLUG) &&
      deal.participation?.includes(BLACK_FRIDAY_PARTICIPATION_SLUG),
    [deal.participation, session?.campaigns],
  );

  const isBlackFridayLastCall = useMemo(
    () =>
      session?.campaigns?.includes(BLACK_FRIDAY_LAST_CALL_SLUG) &&
      deal.participation?.includes(BLACK_FRIDAY_LAST_CALL_PARTICIPATION_SLUG),
    [deal.participation, session?.campaigns],
  );
  const isSumoDay = useMemo(
    () =>
      session?.campaigns?.includes(SUMO_DAY_SLUG) &&
      deal.participation?.includes(SUMO_DAY_PARTICIPATION_SLUG),
    [deal.participation, session?.campaigns],
  );

  return (
    <div className={isSpotlight ? 'relative h-full w-full' : 'relative'}>
      <Image
        src={`${deal.media_url}?width=${width * 2}&height=${
          height * 2
        }&aspect_ratio=16:9`}
        unoptimized
        width={width}
        height={height}
        alt={deal.public_name}
        className={clsx(
          'aspect-sku-card w-full object-cover max-md:rounded',
          className,
        )}
        {...attrs}
      />
      <SkuBanner deal={deal} />
      {isBlackFriday ||
        (isBlackFridayLastCall && (
          <Image
            src={blackFridayBadge}
            alt="Black Friday"
            className="absolute left-0 top-0"
          />
        ))}
      {isSumoDay && (
        <Image
          src={sumodayBadge}
          alt="Sumo Day"
          className="absolute left-0 top-0 w-16 lg:w-24"
        />
      )}
    </div>
  );
};
